import { useState, useEffect } from 'react';
import { Identity, createArticle, DAGObject } from '@amorphous/core';
import './Composer.css';

interface ReplyTo {
  hash: string;
  title: string;
  author: string;
}

interface ComposerProps {
  identity: Identity;
  onPublish: (hash: string) => void;
  onCancel?: () => void;
  addObject: (obj: DAGObject) => Promise<string>;
  broadcastObject?: (obj: DAGObject) => void;
  replyTo?: ReplyTo;
}

export function Composer({ identity, onPublish, onCancel, addObject, broadcastObject, replyTo }: ComposerProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [publishing, setPublishing] = useState(false);

  // Auto-populate title with "Re: [parent title]" for replies
  useEffect(() => {
    if (replyTo) {
      const replyTitle = replyTo.title.startsWith('Re: ') ? replyTo.title : `Re: ${replyTo.title}`;
      setTitle(replyTitle);
    }
  }, [replyTo]);

  async function handlePublish() {
    if (!title.trim() || !body.trim()) return;

    setPublishing(true);
    try {
      const tagList = tags
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(t => t.length > 0);

      const article = createArticle(identity, {
        title: title.trim(),
        body: body.trim(),
        tags: tagList,
        replyTo: replyTo?.hash,
      });

      // Add to DAG storage
      await addObject(article);

      // Broadcast to all connected peers
      if (broadcastObject) {
        broadcastObject(article);
        console.log(`[P2P] Broadcasting new article: ${article.hash.slice(0, 8)}...`);
      }

      onPublish(article.hash);

      // Clear form
      setTitle('');
      setBody('');
      setTags('');
    } finally {
      setPublishing(false);
    }
  }

  const canPublish = title.trim().length > 0 && body.trim().length > 0 && !publishing;

  return (
    <div className="composer">
      <div className="composer-header">
        <h3>{replyTo ? 'Write Reply' : 'Create Article'}</h3>
        {onCancel && (
          <button className="composer-cancel" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>

      {replyTo && (
        <div className="composer-reply-to">
          ↳ Replying to: <strong>{replyTo.title}</strong> by {replyTo.author}
        </div>
      )}

      <input
        type="text"
        className="composer-title"
        placeholder="Article title..."
        value={title}
        onChange={e => setTitle(e.target.value)}
        disabled={publishing}
      />

      <textarea
        className="composer-body"
        placeholder="Write your article in Markdown..."
        value={body}
        onChange={e => setBody(e.target.value)}
        disabled={publishing}
        rows={10}
      />

      <input
        type="text"
        className="composer-tags"
        placeholder="Tags (comma separated)"
        value={tags}
        onChange={e => setTags(e.target.value)}
        disabled={publishing}
      />

      <div className="composer-footer">
        <span className="composer-author mono">
          Signing as: {identity.shortId}
        </span>
        <button
          className="composer-publish"
          onClick={handlePublish}
          disabled={!canPublish}
        >
          {publishing ? 'Publishing...' : 'Publish to Network'}
        </button>
      </div>
    </div>
  );
}
